// 데모 계정(demo · demo2)의 에너지 장치.
//
// 장치 자체(이름·시리얼·종류·fog/edge 역할·좌표)는 관리자 메뉴에서 그 농장에 등록한 것을 그대로 쓴다
// (실계정과 같은 GET /dashboard/{id}/info 의 energyDevices — DemoScenarioContext 가 한 번 읽어 넘긴다).
// 발전·소비·배터리·차트 같은 값만 가짜고, 아래 프로필(DEMO_POLE · DEMO_CUBES)에서 입힌다.
// 등록된 장치가 없으면(또는 아직 못 읽었으면) 화면에도 아무것도 안 그린다 — 관리자에 없는 장치가 보이면 안 된다(10-22).
// 프로필의 이름·시리얼(TB-POLE-01 / DEMO-C1~3)은 registered 를 아예 안 넘기는 테스트에서만 쓰인다.
//   FOG  : 태양광큐브 3대 — fog 1 + edge 2.
//   POLE : 스마트폴 1대.
//
// 정보 탭 카드 · 에너지 탭 · 지도 마커가 모두 여기 하나를 본다. 예전에는 세 곳에 따로 적혀 있어서
// 배터리가 정보 탭 92% / 에너지 탭 87% 로 갈렸고, 지도 툴팁은 문자열로 박혀 있어 따라오지 않았다.
// 값을 바꿀 일이 있으면 이 파일만 고치면 된다.
//
// 응답 모양은 실계정 BE 가 앞으로 내려줄 형태(다중큐브_설계노트 6장)와 같다. BE 가 바뀌면
// 데이터 출처만 바꾸고 화면 코드는 그대로 둔다.

import { toPath } from "../utils/map";

export const DEMO_ENERGY_CONFIGS = ["FOG", "POLE"];
export const DEFAULT_DEMO_ENERGY_CONFIG = "FOG";

// offset 은 데모 드론 홈(getDemoHomePosition) 기준 좌표 차이(도). 0.0001도 ≈ 11m.
export const DEMO_POLE = {
  id: 99201,
  name: "Smart Pole 01",
  serialNum: "DEMO-P1",
  deviceType: "POLE",
  role: null,
  operatingStatus: true,
  isConnected: true,
  // 스마트폴에 달린 축전지. 에너지 탭의 배터리와 같은 값이어야 한다 —
  // 한 장비의 배터리가 화면마다 다르면 시연 중에 바로 질문이 나온다.
  batteryPercent: 87,
  solarW: 428,
  windW: 156,
  consumptionW: 214,
  offset: { lat: 0, lng: 0 },
};

// 큐브는 풍력 발전기가 없다.
// 지도 위치: 셋을 밭 긴 변 바깥에 한 줄로 세운다(demoCubeRow). slot 이 줄에서의 자리 — fog 가 가운데(1), edge 가 양옆(0 · 2).
// rowInsetM 은 그 큐브만 줄에서 밭 쪽으로 당기는 거리(m). 밭 변이 살짝 휘어 한쪽 끝이 떠 보일 때 쓴다.
// offset 은 밭 격자를 모를 때(테스트, 칸이 아직 안 왔을 때) 홈 기준 폴백.
export const DEMO_CUBES = [
  {
    id: 99101,
    name: "Solar Cube 01",
    serialNum: "DEMO-C1",
    deviceType: "CUBE",
    role: "FOG",
    operatingStatus: true,
    isConnected: true,
    batteryPercent: 74,
    solarW: 300,
    windW: 0,
    consumptionW: 200,
    slot: 1,
    offset: { lat: 0, lng: 0 },
  },
  {
    id: 99102,
    name: "Solar Cube 02",
    serialNum: "DEMO-C2",
    deviceType: "CUBE",
    role: "EDGE",
    operatingStatus: true,
    isConnected: true,
    batteryPercent: 66,
    solarW: 250,
    windW: 0,
    consumptionW: 150,
    slot: 0,
    offset: { lat: 0.00024, lng: -0.00032 },
  },
  {
    id: 99103,
    name: "Solar Cube 03",
    serialNum: "DEMO-C3",
    deviceType: "CUBE",
    role: "EDGE",
    operatingStatus: true,
    isConnected: true,
    batteryPercent: 58,
    solarW: 200,
    windW: 0,
    // 발전보다 소비가 커서 fog 에서 전력을 받아야 하는 큐브. 받음/보냄 값이 들어오면 여기서 보인다.
    consumptionW: 250,
    slot: 2,
    rowInsetM: 3,
    offset: { lat: -0.00022, lng: 0.0003 },
  },
];

export const normalizeDemoEnergyConfig = (value) =>
  DEMO_ENERGY_CONFIGS.includes(value) ? value : DEFAULT_DEMO_ENERGY_CONFIG;

// ── 관리자 등록 장치와 합치기 ──────────────────────────────────────
// registered = normalizeInfoEnergyDevices 결과({ mode, devices }) 또는 devices 배열.
const registeredList = (registered) => {
  const list = Array.isArray(registered?.devices)
    ? registered.devices
    : Array.isArray(registered)
      ? registered
      : [];
  return list.filter((device) => device && device.serialNum);
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// 줄에서의 자리: fog(0번)가 가운데, 나머지는 남는 자리를 앞에서부터. 3대면 fog 1, edge 0 · 2.
const slotFor = (index, count) => {
  const mid = Math.floor((count - 1) / 2);
  if (index === 0) return mid;
  const others = Array.from({ length: count }, (_, k) => k).filter(
    (k) => k !== mid,
  );
  return others[index - 1] ?? index;
};

// 프로필(가짜 값)에 등록 장치의 신원을 덮어쓴다.
const withIdentity = (profile, device) => ({
  ...profile,
  id: device.id ?? profile.id,
  name: device.name || profile.name,
  serialNum: device.serialNum,
  deviceType: device.deviceType === "POLE" ? "POLE" : "CUBE",
  role: device.deviceType === "POLE" ? null : (device.role ?? "EDGE"),
  registeredLatitude: toNumberOrNull(device.latitude),
  registeredLongitude: toNumberOrNull(device.longitude),
});

// 등록 장치가 있으면 폴 1대 또는 큐브 N대(fog 먼저)에 프로필을 순서대로 입힌다. 없으면 계정 구성대로 프로필만.
export const demoEnergyDeviceList = (config, registered = undefined) => {
  // registered 를 안 넘기면(undefined) 프로필 그대로 — 테스트용. null(아직 못 읽음·실패)이나 빈 목록이면
  // 등록 장치 없음 → 빈 배열. 관리자에 없는 장치를 화면에 만들어 내지 않는다.
  if (registered === undefined) {
    return normalizeDemoEnergyConfig(config) === "POLE"
      ? [DEMO_POLE]
      : DEMO_CUBES;
  }
  const list = registeredList(registered);
  if (!list.length) return [];
  const pole = list.find((device) => device.deviceType === "POLE");
  if (pole) return [withIdentity(DEMO_POLE, pole)];
  const cubes = [...list].sort(
    (a, b) => (a.role === "FOG" ? 0 : 1) - (b.role === "FOG" ? 0 : 1),
  );
  return cubes.map((device, index) => ({
    ...withIdentity(DEMO_CUBES[index % DEMO_CUBES.length], device),
    slot: slotFor(index, cubes.length),
  }));
};

// 구성도 등록 장치에서 나온다. 폴이 있으면 POLE, fog 있는 큐브 2대 이상이면 FOG, 그 밖의 큐브는 EDGE_ONLY.
export const demoEnergyMode = (config, registered = undefined) => {
  const devices = demoEnergyDeviceList(config, registered);
  if (!devices.length) return "NONE";
  if (devices.some((device) => device.deviceType === "POLE")) return "POLE";
  if (devices.length >= 2 && devices.some((device) => device.role === "FOG"))
    return "FOG";
  return registered === undefined ? "FOG" : "EDGE_ONLY";
};

// ── 큐브 한 줄 배치 ─────────────────────────────────────────────────
// 밭 경계 다각형(boundary, 지도가 흰 선으로 그리는 그 폴리곤)의 긴 변 가운데 드론 홈에 가장 가까운 변 위에
// 1/4 · 1/2 · 3/4 지점으로 세운다. 큐브와 fog 연결선이 경계선에 딱 붙는다.
// 경계가 아직 안 왔으면 격자 칸 중심으로 추정한다(주성분 축 + 칸 중심 범위 + 반 칸). 추정은 경계 밖으로
// 잘린 칸의 중심까지 세어 한 칸쯤 떠 보일 수 있어, 경계가 오면 경계를 쓴다.
// insets[slot] = 그 자리만 밭 안쪽으로 당기는 거리(m). 위도·경도는 미터 평면으로 바꿔 계산한다.
const EDGE_GAP_M = 0.5;
const MIN_FIELD_LENGTH_M = 20;
const M_PER_DEG_LAT = 110540;

// count 대를 변 길이의 1/(count+1) 간격으로. 3대면 1/4 · 1/2 · 3/4.
const fractionsFor = (count) => {
  const n = Math.max(1, count);
  return Array.from({ length: n }, (_, k) => (k + 1) / (n + 1));
};

const metersFrame = (home) => {
  const mPerDegLng = 111320 * Math.cos((home.latitude * Math.PI) / 180);
  return {
    toXY: (p) => ({
      x: (p.longitude - home.longitude) * mPerDegLng,
      y: (p.latitude - home.latitude) * M_PER_DEG_LAT,
    }),
    toLatLng: ({ x, y }) => ({
      latitude: home.latitude + y / M_PER_DEG_LAT,
      longitude: home.longitude + x / mPerDegLng,
    }),
  };
};

// 점(원점 = 홈)에서 선분까지의 거리
const distanceToSegment = (a, b) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t =
    len2 > 0 ? Math.max(0, Math.min(1, (-a.x * dx - a.y * dy) / len2)) : 0;
  return Math.hypot(a.x + dx * t, a.y + dy * t);
};

// 경계 다각형의 변 위. 긴 변(최장 변의 60% 이상) 중 홈에 가장 가까운 변을 고르고,
// 홈에 가까운 끝에서 먼 끝으로 slot 0 → 2 순으로 세운다.
const rowOnBoundary = (polygon, insets, toLatLng, count = 3) => {
  const n = polygon.length;
  const c = polygon.reduce((s, p) => ({ x: s.x + p.x / n, y: s.y + p.y / n }), {
    x: 0,
    y: 0,
  });
  const segments = polygon
    .map((a, i) => {
      const b = polygon[(i + 1) % n];
      return { a, b, len: Math.hypot(b.x - a.x, b.y - a.y) };
    })
    .filter((seg) => seg.len >= MIN_FIELD_LENGTH_M);
  if (!segments.length) return null;
  const maxLen = Math.max(...segments.map((seg) => seg.len));
  const edge = segments
    .filter((seg) => seg.len >= maxLen * 0.6)
    .map((seg) => ({ ...seg, dist: distanceToSegment(seg.a, seg.b) }))
    .sort((p, q) => p.dist - q.dist)[0];

  // 홈에 가까운 끝을 시작점으로
  const [from, to] =
    Math.hypot(edge.a.x, edge.a.y) <= Math.hypot(edge.b.x, edge.b.y)
      ? [edge.a, edge.b]
      : [edge.b, edge.a];
  const d = { x: (to.x - from.x) / edge.len, y: (to.y - from.y) / edge.len };
  // 밭 안쪽을 향하는 법선
  let inward = { x: -d.y, y: d.x };
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  if ((c.x - mid.x) * inward.x + (c.y - mid.y) * inward.y < 0)
    inward = { x: -inward.x, y: -inward.y };

  return fractionsFor(count).map((f, slot) => {
    const t = edge.len * f;
    const inset = (Number(insets[slot]) || 0) - EDGE_GAP_M;
    return toLatLng({
      x: from.x + d.x * t + inward.x * inset,
      y: from.y + d.y * t + inward.y * inset,
    });
  });
};

// 경계가 없을 때: 격자 칸 중심으로 추정
const rowFromCenters = (xy, insets, toLatLng, count = 3) => {
  if (xy.length < 2) return null;
  // 반 칸 = 칸 중심 사이 최소 거리의 절반.
  let minGap = Infinity;
  for (let i = 0; i < xy.length; i += 1) {
    for (let j = i + 1; j < xy.length; j += 1) {
      const dist = Math.hypot(xy[i].x - xy[j].x, xy[i].y - xy[j].y);
      if (dist > 0.5 && dist < minGap) minGap = dist;
    }
  }
  const halfCell = Number.isFinite(minGap) ? minGap / 2 : 0;

  const n = xy.length;
  const c = xy.reduce(
    (sum, p) => ({ x: sum.x + p.x / n, y: sum.y + p.y / n }),
    { x: 0, y: 0 },
  );
  // 주성분: 공분산 행렬의 큰 고유값 방향. 2×2 라 각도로 바로 구한다.
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  xy.forEach((p) => {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  });
  const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  let u = { x: Math.cos(theta), y: Math.sin(theta) }; // 긴 축
  // 축의 방향은 홈(원점)에서 밭 안쪽으로.
  if (c.x * u.x + c.y * u.y < 0) u = { x: -u.x, y: -u.y };
  const v = { x: -u.y, y: u.x }; // 축에 수직

  const along = xy.map((p) => (p.x - c.x) * u.x + (p.y - c.y) * u.y);
  const across = xy.map((p) => (p.x - c.x) * v.x + (p.y - c.y) * v.y);
  const tMin = Math.min(...along);
  const tMax = Math.max(...along);
  if (tMax - tMin < MIN_FIELD_LENGTH_M) return null;
  const sMin = Math.min(...across);
  const sMax = Math.max(...across);
  // 홈이 축의 어느 쪽에 있는지 — 그쪽 가장자리에 세운다.
  const homeAcross = (0 - c.x) * v.x + (0 - c.y) * v.y;
  const sideSign = homeAcross >= 0 ? 1 : -1;
  const s =
    sideSign > 0 ? sMax + halfCell + EDGE_GAP_M : sMin - halfCell - EDGE_GAP_M;

  return fractionsFor(count).map((f, slot) => {
    const t = tMin + (tMax - tMin) * f;
    const sk = s - sideSign * (Number(insets[slot]) || 0); // 밭 쪽으로 당김
    return toLatLng({
      x: c.x + u.x * t + v.x * sk,
      y: c.y + u.y * t + v.y * sk,
    });
  });
};

export const demoCubeRow = (
  home,
  centers = [],
  insets = [],
  boundary = null,
  count = 3,
) => {
  if (!home) return null;
  const { toXY, toLatLng } = metersFrame(home);
  const polygon = toPath(boundary).map((p) =>
    toXY({ latitude: p.lat, longitude: p.lng }),
  );
  if (polygon.length >= 3) {
    const row = rowOnBoundary(polygon, insets, toLatLng, count);
    if (row) return row;
  }
  const points = (centers ?? []).filter(
    (point) =>
      point &&
      Number.isFinite(point.latitude) &&
      Number.isFinite(point.longitude),
  );
  return rowFromCenters(points.map(toXY), insets, toLatLng, count);
};

const placeDevice = (device, home, row) => {
  // 관리자가 좌표를 넣어 뒀으면 그 자리. (0, 0)은 "안 넣음"으로 본다.
  const lat = device.registeredLatitude;
  const lng = device.registeredLongitude;
  if (lat != null && lng != null && !(lat === 0 && lng === 0)) {
    return { latitude: lat, longitude: lng };
  }
  if (!home) return { latitude: null, longitude: null };
  if (row && Number.isInteger(device.slot) && row[device.slot])
    return row[device.slot];
  return {
    latitude: home.latitude + device.offset.lat,
    longitude: home.longitude + device.offset.lng,
  };
};

// 정보 탭 · 지도. 실계정 GET /dashboard/{id}/info 의 energyDevices 와 같은 모양.
// home 이 없으면(지도 칸이 아직 안 왔으면) 좌표는 null — 지도가 그 장치를 건너뛴다.
// 큐브 좌표: 관리자 좌표 > 밭 경계(boundary) 변 위 한 줄 > 격자 칸 중심 추정 > 홈 기준 offset 순.
// registered = 관리자 등록 장치(없으면 프로필 폴백).
export const demoEnergyDevicesInfo = (
  config,
  home = null,
  { now = new Date(), centers = null, boundary = null, registered } = {},
) => {
  const devices = demoEnergyDeviceList(config, registered);
  const cubeCount = devices.filter(
    (device) => device.deviceType === "CUBE",
  ).length;
  const insets = [];
  devices.forEach((device) => {
    if (Number.isInteger(device.slot))
      insets[device.slot] = device.rowInsetM ?? 0;
  });
  const row = cubeCount
    ? demoCubeRow(home, centers, insets, boundary, cubeCount)
    : null;
  return {
    mode: demoEnergyMode(config, registered),
    devices: devices.map((device) => ({
      id: device.id,
      name: device.name,
      serialNum: device.serialNum,
      deviceType: device.deviceType,
      role: device.role,
      ...placeDevice(device, home, row),
      operatingStatus: device.operatingStatus,
      batteryCharge: device.batteryPercent,
      isConnected: device.isConnected,
      lastSeenAt: new Date(now.getTime() - 8 * 1000).toISOString(),
    })),
  };
};
